import { useMemo } from 'react';
import { useUserStore } from '@/stores';
import { readUsername, useCurrentUsername } from './useCurrentUsername';

export type ChatModel =
  | 'gpt-4o'
  | 'gpt-5.4-mini'
  | 'gpt-5.4'
  | 'gpt-5.5'
  | 'gpt-5.6-mini'
  | 'gpt-5.6'
  | 'gpt-6-astra'
  | 'claude-3.7-sonnet'
  | 'claude-4-sonnet'
  | 'claude-4-opus';

export type ChatModelProvider = 'openai' | 'anthropic';

export interface ChatModelOption {
  value: ChatModel;
  label: string;
  provider: ChatModelProvider;
  description: string;
  /** Rough relative cost indicator, 1 (cheapest) – 4 (priciest). */
  costTier: 1 | 2 | 3 | 4;
  /** Shown in the pickers but greyed out and not selectable. */
  disabled?: boolean;
  /** Set when `disabled`: what the reader should do to get access. */
  requestNote?: string;
}

/** Where to write about a model that is not open to this account. */
export const REQUEST_ACCESS_EMAIL = 'info@patentloop.com';
export const REQUEST_ACCESS_NOTE = `Contact ${REQUEST_ACCESS_EMAIL} to request access to this model.`;

/**
 * The accounts that may use the models still in trial.
 *
 * The real gate is server-side, in `util/model_registry.py`: this only decides what is
 * greyed out, and a request that gets past the UI is refused there and falls back to
 * the tool's default. Matched case-insensitively, and against the part before the "@"
 * when the username is an email address.
 */
const EARLY_ACCESS_USERS = ['jim', 'jimmy'];

function identities(username: string): string[] {
  const name = (username || '').trim().toLowerCase();
  if (!name) return [];
  return name.includes('@') ? [name, name.split('@')[0]] : [name];
}

function hasEarlyAccess(username: string): boolean {
  return identities(username).some(id => EARLY_ACCESS_USERS.includes(id));
}

/**
 * Every model the product can call, keyed by the id the backend and the settings hold.
 *
 * Keep in sync with `util/model_registry.py`, which is the authority: it also holds the
 * provider's own id for each one ("claude-4-sonnet" → "claude-sonnet-4-20250514"),
 * which is translated server-side at the call. Nothing here ever sends a provider id.
 */
type CatalogEntry = Omit<ChatModelOption, 'value' | 'disabled' | 'requestNote'> & {
  /** 'request' models are shown greyed out for everyone but the early-access accounts. */
  access?: 'open' | 'request';
};

export const MODEL_CATALOG: Record<ChatModel, CatalogEntry> = {
  'gpt-4o': {
    label: 'GPT-4o',
    provider: 'openai',
    description: 'Fast, well-rounded',
    costTier: 2,
  },
  'gpt-5.4-mini': {
    label: 'GPT-5.4 Mini',
    provider: 'openai',
    description: 'Cheapest, quick responses',
    costTier: 1,
  },
  'gpt-5.4': {
    label: 'GPT-5.4',
    provider: 'openai',
    description: 'Balanced quality and cost',
    costTier: 2,
  },
  'gpt-5.5': {
    label: 'GPT-5.5',
    provider: 'openai',
    description: 'Highest-quality OpenAI model',
    costTier: 3,
  },
  'gpt-5.6-mini': {
    access: 'request',
    label: 'GPT-5.6 Mini',
    provider: 'openai',
    description: 'The 5.6 small tier. In trial.',
    costTier: 2,
  },
  'gpt-5.6': {
    access: 'request',
    label: 'GPT-5.6',
    provider: 'openai',
    description: 'The 5.6 standard tier. In trial.',
    costTier: 3,
  },
  'gpt-6-astra': {
    access: 'request',
    label: 'GPT-6 Astra',
    provider: 'openai',
    description: 'The newest and most capable. In trial.',
    costTier: 4,
  },
  'claude-3.7-sonnet': {
    // On request for every account.
    access: 'request',
    label: 'Claude 3.7 Sonnet',
    provider: 'anthropic',
    description: 'Cheaper Sonnet, strong on long documents',
    costTier: 2,
  },
  'claude-4-sonnet': {
    // On request for every account.
    access: 'request',
    label: 'Claude 4 Sonnet',
    provider: 'anthropic',
    description: 'Strong reasoning, large context',
    costTier: 2,
  },
  'claude-4-opus': {
    // On request for every account.
    access: 'request',
    label: 'Claude 4 Opus',
    provider: 'anthropic',
    description: 'Most capable, most expensive',
    costTier: 4,
  },
};

/** Whether this account may actually run a model. */
export function isModelAllowed(value: ChatModel, username: string): boolean {
  const entry = MODEL_CATALOG[value];
  if (!entry || entry.access !== 'request') return true;
  // Early-access accounts may run everything, Anthropic included: they are the ones who
  // decide what to open next, and a model they cannot try is a model they cannot judge.
  // Mirrors `is_allowed` in `util/model_registry.py`, which is the real gate.
  return hasEarlyAccess(username);
}

function optionsFor(ids: ChatModel[], username: string): ChatModelOption[] {
  return ids.map(value => {
    const { access, ...entry } = MODEL_CATALOG[value];
    void access;
    const allowed = isModelAllowed(value, username);
    // Listed either way. A model nobody can see cannot be asked for, and being asked
    // is the point of showing it.
    return allowed
      ? { value, ...entry }
      : { value, ...entry, disabled: true, requestNote: REQUEST_ACCESS_NOTE };
  });
}

// The main chat at "/". Cheap models belong here in a way they do not in drafting:
// most questions are short and the reader is waiting.
const CHAT_MODEL_IDS: ChatModel[] = [
  'gpt-4o',
  'gpt-5.4-mini',
  'gpt-5.4',
  'gpt-5.5',
  'gpt-5.6-mini',
  'gpt-5.6',
  'gpt-6-astra',
  'claude-4-sonnet',
  'claude-4-opus',
];

/**
 * The chat list as seen by an account with no special access.
 *
 * For looking a label up, not for rendering a picker: every on-request model is
 * disabled in it whoever is signed in. Pickers use `options` from `useChatModel()`.
 */
export const CHAT_MODEL_OPTIONS: ChatModelOption[] = optionsFor(CHAT_MODEL_IDS, '');

// /drafting and the application tool: the specification, the claims, the outline.
// Quality first — a claim drafted cheaply is not a saving — so no nano tier.
const DRAFTING_MODEL_IDS: ChatModel[] = [
  'gpt-5.5',
  'gpt-5.4',
  'gpt-5.4-mini',
  'gpt-6-astra',
  'gpt-5.6',
  'gpt-5.6-mini',
  'claude-4-opus',
  'claude-4-sonnet',
];

/** As above: labels only. Pickers use `options` from `useToolModel()`. */
export const DRAFTING_MODEL_OPTIONS: ChatModelOption[] = optionsFor(DRAFTING_MODEL_IDS, '');

export function chatModelOptionsFor(username: string): ChatModelOption[] {
  return optionsFor(CHAT_MODEL_IDS, username);
}

export function draftingModelOptionsFor(username: string): ChatModelOption[] {
  return optionsFor(DRAFTING_MODEL_IDS, username);
}

export const DEFAULT_CHAT_MODEL: ChatModel = 'gpt-4o';

/**
 * `username` is REQUIRED, deliberately.
 *
 * It used to default to "" — which reads as "an account with no special access", so a
 * caller that forgot it silently downgraded every on-request model to the default. That
 * happened in the send path, where the picked model was replaced before the request left
 * the browser. A required parameter makes the compiler catch the next one.
 */
export function isChatModelSelectable(
  value: ChatModel | string | undefined,
  username: string
): boolean {
  if (!value || !(value in MODEL_CATALOG)) return false;
  if (!CHAT_MODEL_IDS.includes(value as ChatModel)) return false;
  return isModelAllowed(value as ChatModel, username);
}

export function useChatModel() {
  const username = useCurrentUsername();
  const chatModel = useUserStore(state => {
    const stored = state.userSettings.chatModel as ChatModel | undefined;
    // Fall back if the stored model is one this account may no longer use — access can
    // be withdrawn, and a setting saved while it was open should not strand the picker.
    return stored && isChatModelSelectable(stored, readUsername()) ? stored : DEFAULT_CHAT_MODEL;
  });
  const updateUserSettings = useUserStore(state => state.updateUserSettings);
  const options = useMemo(() => chatModelOptionsFor(username), [username]);

  const setChatModel = (value: ChatModel) => {
    if (!isChatModelSelectable(value, username)) return;
    updateUserSettings('chatModel', value);
  };

  const getModelLabel = (): string => MODEL_CATALOG[chatModel]?.label || 'GPT-4o';

  return { chatModel, setChatModel, getModelLabel, options };
}

/**
 * The model a tool uses for every one of its sessions.
 *
 * Per account rather than per session or per run: a drafter picks how their
 * applications get written once, and every step of every application follows it. The
 * backend reads the same setting when it queues the work, so a page that never sends a
 * model still gets the right one.
 *
 * `tool` is the settings key — `draftingModel` for /drafting, `applicationModel` for
 * the application tool at "/". Both are validated server-side against
 * `util/model_registry.py`, so a stale client cannot pick something unoffered.
 */
export type ToolModelSetting = 'draftingModel' | 'applicationModel' | 'actionModel';

export const DEFAULT_TOOL_MODEL: ChatModel = 'gpt-5.5';

export function useToolModel(setting: ToolModelSetting) {
  const username = useCurrentUsername();
  const model = useUserStore(state => {
    const stored = state.userSettings[setting] as ChatModel | undefined;
    const offered =
      !!stored && DRAFTING_MODEL_IDS.includes(stored) && isModelAllowed(stored, readUsername());
    return offered && stored ? stored : DEFAULT_TOOL_MODEL;
  });
  const updateUserSettings = useUserStore(state => state.updateUserSettings);
  const options = useMemo(() => draftingModelOptionsFor(username), [username]);

  const setModel = (value: ChatModel) => {
    if (!DRAFTING_MODEL_IDS.includes(value) || !isModelAllowed(value, username)) return;
    updateUserSettings(setting, value);
  };

  const label = MODEL_CATALOG[model]?.label ?? model;
  return { model, setModel, label, options };
}
